import { Logger, NOOP_PROMISE_VOID, NOOP_VOID, Presenter } from 'wdc-cube'

import { WaitingScope } from '../../the-connection/tc_scopes'
import ConfigurationPresenter from './presenter'
import { type MailTemplateOption, MailTemplateRow, MailTemplateScope } from './scopes'
import { MailConfigService } from './service'

import * as Handlebars from 'handlebars'

const LOG = Logger.get('MailTemplatePresenter')
const service = MailConfigService.singleton()

const templateOptionsVariablesMap = new Map<MailTemplateOption, string[]>()

export class EmailTemplatePresenter extends Presenter<MailTemplateScope, ConfigurationPresenter> {
    // :: Constructor

    public constructor(owner: ConfigurationPresenter) {
        super(owner, owner.scope.emailTemplate)
        this.__parent = owner
    }

    // :: Instance

    private readonly __parent: ConfigurationPresenter

    private _yesAction = NOOP_PROMISE_VOID

    // :: Methods

    public override release() {
        super.release()
    }

    async initialize() {
        LOG.debug('initialize')

        initializeStatics()

        this.scope.onTemplateTypeChanged = this.action(this.handleTemplateTypeChanged)

        this.scope.onFind = this.action(this.handleFind)
        this.scope.onEdit = this.action(this.handleEdit)
        this.scope.onDelete = this.action(this.handleDelete)
        this.scope.onNew = this.action(this.handleNew)
        this.scope.onYesNoResponse = this.action(this.handYesNoResponse)

        this.scope.onClose = this.action(this.handleClose)
        this.scope.onSave = this.action(this.handleSave)
        this.scope.onContentChanged = this.action(this.handleContentChanged)

        await this.onLoadData()
    }

    async onLoadData() {
        await this.handleFind(this.scope.templateFilter)
    }

    private async handleFind(nameFilter?: string) {
        try {
            this.scope.waiting = new WaitingScope()
            this.scope.templateFilter = nameFilter || ''
            const templateRows = await service.fetchTemplates(this.scope.templateFilter)
            this.scope.templates = templateRows
        } catch (caught) {
            LOG.error('handleFind', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    private async handleEdit(templateId: number) {
        await this.buscarTemplateSelecionado(templateId)
        this.scope.tab = 'form'
    }

    private async handleDelete(templateId: number) {
        this.scope.askYesOrNo = 'Deseja excluir o Template?'
        this._yesAction = async () => {
            try {
                await service.deleteTemplate(templateId)
                await this.handleFind(this.scope.templateFilter)
                this._yesAction = NOOP_PROMISE_VOID
                this.__parent.app.alert('success', 'Sucesso', 'Registro excluído com sucesso')
            } catch (error) {
                LOG.caught(error)
                this.__parent.app.alert('error', 'Problemas', 'Não foi possível excluir o registro. Tente mais tarde')
            }
        }
    }

    protected async handYesNoResponse(yes: boolean) {
        LOG.debug(`handYesNoResponse: yes=${yes}`)
        this.scope.askYesOrNo = ''
        if (yes) {
            await this._yesAction()
        }
        this.scope.update()
    }

    private async handleNew() {
        this.scope.templateId = undefined
        this.action(this.handleTemplateTypeChanged)('welcome')
        this.scope.templateName = ''
        this.scope.subject = ''
        this.scope.content = ''
        this.scope.previewHtml = ''
        this.scope.variables = [] as string[]

        this.scope.tab = 'form'
    }

    private async handleClose() {
        this.scope.tab = 'list'
    }

    private async handleSave() {
        try {
            if (!this.scope.templateName) {
                this.__parent.app.notify('warning', 'Nome do template deve ser informado')
                return
            }
            if (!this.scope.content) {
                this.__parent.app.notify('warning', 'Conteúdo do template deve ser informado')
                return
            }
            if (!this.checkTemplate(this.scope.content)) {
                this.__parent.app.notify('error', 'Conteúdo do template está com problemas. Verifique as variáveis!')
                return
            }

            const formData = {
                name: this.scope.templateName,
                templateType: this.scope.templateType,
                subject: this.scope.subject,
                templateContent: this.scope.content
            } as MailTemplateRow

            const retSalvar = this.scope.templateId
                ? await service.updateTemplate(this.scope.templateId, formData)
                : await service.createTemplate(formData)

            if (retSalvar) {
                this.action(this.handleClose)()
                await this.handleFind()
                this.__parent.app.notify('success', 'Salvo com sucesso')
            }
        } catch (caught) {
            LOG.error('handleSave', caught)
            throw caught
        }
    }

    private async handleTemplateTypeChanged(newType: MailTemplateOption) {
        this.scope.templateType = newType
        this.scope.availableVariables = templateOptionsVariablesMap.get(newType) || []
    }

    private async handleContentChanged(content: string) {
        try {
            this.scope.content = content
            if (!content) {
                this.scope.variables = []
                this.scope.previewHtml = ''
                return
            }
            const safeContent = content.includes('{{}}') ? content.replaceAll('{{}}', '{}') : content
            const { vars, mock } = this.mockVars(safeContent)

            const compiledTemplate = Handlebars.compile(safeContent)
            const html = compiledTemplate(mock)

            this.scope.variables = vars
            this.scope.previewHtml = html
        } catch (error) {
            LOG.error(String(error))
        }
    }

    private checkTemplate(templateContent: string) {
        try {
            if (!templateContent) {
                return true
            }

            const { mock } = this.mockVars(templateContent)

            const compiledTemplate = Handlebars.compile(templateContent)
            const html = compiledTemplate(mock)

            return !!html
        } catch (error) {
            return false
        }
    }

    buscarTemplateSelecionado = async (templateId?: number) => {
        if (!templateId) return

        try {
            this.scope.waiting = new WaitingScope()
            LOG.debug(`buscarTemplateSelecionado ${templateId}`)
            const template = await service.fetchTemplateById(templateId)
            if (template) {
                this.scope.templateId = template.seq
                this.action(this.handleTemplateTypeChanged)(template.templateType)
                this.scope.templateName = template.name
                this.scope.subject = template.subject

                this.action(this.handleContentChanged)(template.templateContent)
            }
        } finally {
            this.scope.waiting = undefined
        }
    }

    mockVars = (template: string) => {
        const regex = /{{\s*([\w.]+)\s*}}/g
        const matches = new Set<string>()
        let match

        while ((match = regex.exec(template)) !== null) {
            matches.add(match[1])
        }

        const vars = Array.from(matches)
        const mock: Record<string, string> = {}

        vars.forEach((v) => {
            mock[v] = v.toUpperCase()
        })
        return { vars, mock }
    }
}

export default EmailTemplatePresenter

let initializeStatics = () => {
    templateOptionsVariablesMap.set('welcome', ['email', 'name', 'password'])
    templateOptionsVariablesMap.set('password-recovery', ['email', 'name', 'recovery_link'])
    templateOptionsVariablesMap.set('new-password-generated', ['email', 'name', 'new_password'])

    initializeStatics = NOOP_VOID
}
